"use client"

import { useState } from "react"
import { Card, CardMedia, CardContent, Typography, Box, Chip, Grid, Paper, Stack, Divider, Button,IconButton,Dialog } from "@mui/material"
import {
  Favorite as HeartIcon,
  Bookmark as BookmarkIcon,
  CalendarToday as CalendarIcon,
  Visibility as EyeIcon,
  VisibilityOff as EyeOffIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon

} from "@mui/icons-material"
import { format } from "date-fns"
import { Document, Page, pdfjs } from "react-pdf"

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const PostCard = ({ post }) => {
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pdfError, setPdfError] = useState(null)

  const formatDate = (date) => {
    if (date) return format(new Date(date), "MMM dd, yyyy")
    return "Not specified"
  }

  const formatType = (type) => {
    return type.replace(/([A-Z])/g, " $1").trim()
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setPdfError(null)
  }

  const onDocumentLoadError = (error) => {
    console.error("Error loading PDF:", error)
    setPdfError("Failed to load PDF")
  }

  const isPdf = post.photo && post.photo.url.toLowerCase().endsWith(".pdf")

  const handleOpenPdf = () => {
    window.open(post.photo.url, "_blank")
  }

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        transition: "box-shadow 0.3s",
        "&:hover": {
          boxShadow: 6,
        },
      }}
    >
      {post.photo && (
        <Box sx={{ position: "relative", height: 200 }}>
          {!isPdf && post.photo.type === "image" && (
            <CardMedia
              component="img"
              height="200"
              image={`${post.photo.url}`}
              alt={post.subject_line}
              sx={{ objectFit: "cover" }}
            />
          )}
          {!isPdf && post.photo.type === "video" && (
            <CardMedia
              component="video"
              height="200"
              controls
              src={`${post.photo.url}`}
              alt={post.subject_line}
              sx={{ objectFit: "cover" }}
            />
          )}
          {!isPdf && post.photo.type === "audio" && (
            <Box
              sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, bgcolor: "#e0e0e0" }}
            >
              <audio controls src={`${post.photo.url}`}>
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}
          {isPdf && (
            <Box
              sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, bgcolor: "#e0e0e0" }}
            >
              {pdfError ? (
                <>
                  <Typography color="error" gutterBottom>
                    {pdfError}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<OpenInNewIcon />}
                    onClick={handleOpenPdf}
                    sx={{
                      borderRadius: 20,
                      textTransform: "none",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                      },
                    }}
                  >
                    Open PDF in New Tab
                  </Button>
                </>
              ) : (
                <Box
                  onClick={() => setShowPdfModal(true)}
                  sx={{
                    // height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    
                    cursor: "pointer",
                    transition: "background-color 0.3s",
                    "&:hover": {
                      bgcolor: "#e0e0e0",
                    },
                  }}
                >

<PdfIcon sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to view PDF
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              display: "flex",
              gap: 1,
            }}
          >
            <Chip
              label={formatType(post.type)}
              size="small"
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(4px)",
              }}
            />
            <Chip
              icon={post.feed_show_status ? <EyeIcon /> : <EyeOffIcon />}
              label={post.feed_show_status ? "Visible" : "Hidden"}
              size="small"
              color={post.feed_show_status ? "success" : "error"}
              sx={{
                bgcolor: post.feed_show_status ? "primary" : "rgba(255, 200, 200, 0.9)",
              }}
            />
          </Box>
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {post.subject_line || "Untitled"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mb: 2,
            }}
          >
            {post.input_text || "No description available"}
          </Typography>
        </Box>

        {(post.picture_1 || post.picture_2) && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {post.picture_1 && (
              <Box
                component="img"
                src={`${post.picture_1.url}`}
                alt="Additional preview 1"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
              />
            )}
            {post.picture_2 && (
              <Box
                component="img"
                src={`${post.picture_2.url}`}
                alt="Additional preview 2"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
              />
            )}
          </Stack>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
          <Typography variant="subtitle2" gutterBottom>
            Event Schedule
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Main Event
              </Typography>
              <Typography variant="body2">
                {formatDate(post?.startDay ?? null)} - {formatDate(post?.endDay ?? null)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Welcome Period
              </Typography>
              <Typography variant="body2">
                {formatDate(post?.welcomeStartDay ?? null)} - {formatDate(post?.welcomeEndDay ?? null)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ mt: "auto" }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <HeartIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {post.like_count}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <BookmarkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {post.wishlist_count}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {formatDate(post.startDay)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
      <Dialog
        open={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#FFFFFF",
            backgroundImage: "linear-gradient(to bottom, #f7f7f7, #ffffff)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          },
        }}
        TransitionProps={{
          timeout: 400,
        }}
      >
        <Box
          sx={{
            position: "relative",
            p: 4,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1a1a1a",
                fontSize: "1.25rem",
              }}
            >
              {post.subject_line || "View PDF"}
            </Typography>
            <IconButton
              onClick={() => setShowPdfModal(false)}
              sx={{
                color: "grey.500",
                transition: "all 0.2s",
                "&:hover": {
                  transform: "scale(1.1)",
                  color: "grey.700",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#ffffff",
              borderRadius: 2,
              p: 3,
              boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)",
            }}
          >
            {post.photo && isPdf && (
              <Document
                file={post.photo.url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <PdfIcon sx={{ fontSize: 48, color: "primary.main" }} />
                    <Typography sx={{ color: "text.secondary" }}>
                      Loading PDF...
                    </Typography>
                  </Box>
                }
              >
                {pdfError ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      color: "error.main",
                    }}
                  >
                    <Typography variant="h6" color="error">
                      {pdfError}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => window.open(post.photo.url, "_blank")}
                    >
                      Open in New Tab
                    </Button>
                  </Box>
                ) : (
                  <Page
                    pageNumber={pageNumber}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    height={window.innerHeight * 0.6}
                    className="pdf-page"
                  />
                )}
              </Document>
            )}
          </Box>

          {numPages && !pdfError && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderTop: "1px solid",
                borderColor: "grey.100",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<PrevIcon />}
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: 1,
                  },
                  transition: "all 0.2s",
                }}
              >
                Previous
              </Button>
              <Typography
                variant="body2"
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                Page {pageNumber} of {numPages}
              </Typography>
              <Button
                variant="outlined"
                endIcon={<NextIcon />}
                onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                disabled={pageNumber >= numPages}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: 1,
                  },
                  transition: "all 0.2s",
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>
    </Card>

  )
}

export default PostCard

